import { useAnalyticContext } from '@application/analytic/analytic_link/mod';
import { useFeatureFlag } from '@application/feature_flags/use_feature_flag';
import { withTranslation } from '@application/i18n/with_translation';
import { DateService } from '@application/time/date.service';
import { validateRequired } from '@application/validators';
import {
	BadgeText,
	Box,
	ButtonArrange,
	CellulaFormSubmitData,
	DialogAlert,
	Divider,
	Flex,
	Form,
	Icon,
	InputCheckbox,
	InputCheckboxGroup,
	InputTextArea,
	Label,
	Sheet,
	Text,
	TextLink,
} from '@cellula/react';
import { primitiveColorTokens, tokens } from '@cellula/react-theme-patient';
import { AvatarInfo } from '@component/avatar_info.component';
import { DataList } from '@component/data_list.component';
import { IframeSheet } from '@component/iframe_sheet.component';
import { Title } from '@component/title.component';
import { TopBar } from '@component/top_bar.component';
import { FormCard } from '@feature/dynamic_form/form_card.component';
import { FileUpload } from '@feature/file_manager/file_upload.component';
import { FileManagerServiceReadyState } from '@feature/file_manager/mod';
import { Payment } from '@feature/payment/payment.component';
import { capitalizeFirstLetter } from '@module/capitalize_first_letter';
import { useQuery } from '@tanstack/react-query';
import type { PhysicalLocationApiModel, ResourceModel } from '@visiba/api-vcs-client-v2';
import { clientIdentityGetOptions, clientIdentityInviteablePersonsOptions } from '@visiba/api-vcs-client-v2';
import { useResolve } from '@visiba/instantiation';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { BookingScreen, useBooking, ViewBookingAnalyticActions } from '../../hooks/use_booking';
import { useBookingBasket } from '../../hooks/use_booking_basket';
import { SlotNotAvailableError, TooCloseToAppointmentStartError, useScreenConclusion } from './use_screen_conclusion';

const StyledTermsOfServiceSection = styled.div`
	text-align: center;
	font: ${tokens.text.label.smallRegular};

	${tokens.breakpointQuery.small} {
		text-align: left;
	}
`;

const REASON_MAX_LENGTH = 4000;

export function ScreenConclusion() {
	const screenConclusion = useScreenConclusion();
	const bookingBasket = useBookingBasket();
	const viewBooking = useBooking();
	const featureUseRepresentatives = useFeatureFlag('PatientWeb.UseRepresentatives');
	const [payment, setPayment] = useState<{ paymentBillId: number; paymentAmount: string; ticket: string } | null>(null);
	const [disableSubmit, setDisableSubmit] = useState<string | false>(false);
	const analytic = useAnalyticContext();

	const { flowSettings } = viewBooking;
	if (flowSettings.status !== 'accessible') {
		return null;
	}

	const basket = bookingBasket.getBasket();
	const isRescheduling = !!basket.rescheduleTicket;

	function handleFileManagerStateChange(state: FileManagerServiceReadyState) {
		analytic.trackEvent('Changed "attachments"');
		setDisableSubmit(
			state === FileManagerServiceReadyState.NotReady
				? withTranslation('View.CreateNewMessage.ScreenConclusion.AttachedFilesNotReady')
				: false,
		);
	}

	async function handleSubmit(formData: CellulaFormSubmitData) {
		const submitResult = await screenConclusion.submit({
			...formData,
		});

		if (submitResult.err) {
			if (submitResult.err.message === 'SlotNotAvailableError') {
				return false;
			}

			if (submitResult.err.message === 'UnansweredFormsError') {
				return Err(withTranslation('View.Booking.ScreenConclusion.FormValidationError'));
			}

			if (submitResult.err.message === 'TooCloseToAppointmentStartError') {
				return Err(withTranslation('View.Booking.ScreenConclusion.TooCloseToAppointmentStartError'));
			}

			return Err(withTranslation('View.Booking.ScreenConclusion.SubmitError'));
		}

		if (submitResult.ok.payment) {
			setPayment({
				paymentBillId: submitResult.ok.payment.billId,
				paymentAmount: submitResult.ok.payment.amount,
				ticket: submitResult.ok.ticket,
			});
		} else {
			viewBooking.goToScreen(BookingScreen.Done, {
				ticket: submitResult.ok.ticket,
				reschedule: isRescheduling,
			});
		}

		return Ok();
	}

	function handleInvalidSubmit() {
		analytic.trackEvent(ViewBookingAnalyticActions.FormIsInvalid);
	}

	async function handlePaymentSuccess() {
		viewBooking.goToScreen(BookingScreen.Done, {
			ticket: payment?.ticket,
			reschedule: isRescheduling,
		});

		return Ok();
	}

	function handlePaymentAbort() {
		setPayment(null);
		screenConclusion.abortPayment();
	}

	function handleMessageChange() {
		analytic.trackEvent('Changed "message"');
	}

	function handleSubmitClick(requirePayment: boolean) {
		return () => {
			if (requirePayment) {
				analytic.trackEvent('Clicked pay and book time slot');
			} else {
				analytic.trackEvent('Clicked book time slot');
			}
		};
	}

	const showReasonTextarea = !flowSettings.value.descriptionOfMatter;
	const showAttachFiles = flowSettings.value.attachFiles;

	const getSubmitButtonLabel = (getRequirePayment: () => boolean): string => {
		if (isRescheduling) {
			return withTranslation('Appointment.AppointmentInfo.Rebooking.Confirm.Button');
		}
		if (getRequirePayment()) {
			return withTranslation('View.Booking.ScreenConclusion.ButtonWithPayment');
		}
		return withTranslation('View.Booking.ScreenConclusion.ButtonWithoutPayment');
	};

	const getRequirePayment = (): boolean => screenConclusion.data.value!.requirePayment;

	return (
		<>
			<TopBar.Content goBack />

			<Flex grow={1} gap={tokens.spacing.x2}>
				{screenConclusion.data.status === 'accessible' && screenConclusion.data.value ? (
					<>
						<Flex grow={1} gap={tokens.spacing.x2}>
							<Title title={withTranslation('View.Booking.ScreenConclusion.Title')} titleSize='small' />

							<Form.Root options={{}} onSubmit={handleSubmit} onInvalid={handleInvalidSubmit}>
								<Flex gap={tokens.spacing.x3} grow={1}>
									{isRescheduling ? (
										<Text variant='bodyRegular'>{withTranslation('Appointment.AppointmentInfo.Rebooking.Confirm.Title')}</Text>
									) : null}
									{!isRescheduling ? (
										<Flex gap={tokens.spacing.x2}>
											{showReasonTextarea ? (
												<InputTextArea
													name='reason'
													label={withTranslation('View.Booking.ScreenConclusion.Input.Label.Reason')}
													helperText={withTranslation('View.Booking.ScreenConclusion.Input.HelpText.Reason')}
													minHeight={120}
													maxHeight={400}
													autoHeight
													required
													characterCount={REASON_MAX_LENGTH}
													onBlur={handleMessageChange}
													options={{
														validate: {
															required: (v) =>
																validateRequired(v) ||
																withTranslation('View.Booking.ScreenConclusion.ValidationError.Required.Reason'),
														},
														maxLength: {
															value: REASON_MAX_LENGTH,
															message: withTranslation('View.Booking.ScreenConclusion.ValidationError.MaxLength.Reason', {
																maxLength: `${REASON_MAX_LENGTH}`,
															}),
														},
													}}
												/>
											) : null}
											{showAttachFiles ? (
												<Flex gap={tokens.size.x1}>
													<fieldset style={{ display: 'contents' }}>
														<Label type='legend' optional>
															{withTranslation('Form.Input.Label.AttachedFiles')}
														</Label>
														<FileUpload.Root identityEndpointPath='client/v2/userfiles' onStateChange={handleFileManagerStateChange}>
															<FileUpload.List />
															<FileUpload.DropZone>
																<FileUpload.Button />
															</FileUpload.DropZone>
														</FileUpload.Root>
													</fieldset>
												</Flex>
											) : null}
										</Flex>
									) : null}

									<TimeSlotInfo
										title={withTranslation('View.Booking.TimeSlotInfo.Title')}
										timeSlot={{
											price: screenConclusion.data.value.timeSlot.Price?.Value
												? screenConclusion.data.value.timeSlot.Price?.Formatted
												: undefined,
											asAgentFor: screenConclusion.data.value.asAgentFor ?? undefined,
											reasonForVisit: screenConclusion.data.value.reasonForVisit ?? undefined,
											duration: screenConclusion.data.value.timeSlot.Duration,
											physicalLocation: screenConclusion.data.value.physicalLocation,
											resource: screenConclusion.data.value.timeSlot.Resource,
											start: screenConclusion.data.value.timeSlot.Start,
										}}
									/>

									{!isRescheduling && bookingBasket.formManager.getForms().length > 0 ? (
										<Flex gap={tokens.spacing.x1}>
											<Text variant='labelSemiBold'>{withTranslation('Common.Label.AnsweredForms')}</Text>
											<Flex gap={tokens.spacing.x1}>
												{bookingBasket.formManager.getForms().map((form) => (
													<FormCard key={form.formId} formWorker={form} analyticAction='' onCompleted={false} />
												))}
											</Flex>
										</Flex>
									) : null}

									{featureUseRepresentatives && !isRescheduling ? <BookingParticipants /> : null}

									<Flex grow='1' justify='flex-end' gap={tokens.spacing.x2}>
										<Form.Error />
										<StyledTermsOfServiceSection>
											{withTranslation('View.CreateNewMessage.ScreenConclusion.Terms.Part1')}{' '}
											<IframeSheet
												src={screenConclusion.termsOfServiceUrl ?? ''}
												trigger={
													<TextLink as='button' size='small'>
														{withTranslation('View.CreateNewMessage.ScreenConclusion.Terms.Link')}
													</TextLink>
												}
											/>{' '}
											{withTranslation('View.CreateNewMessage.ScreenConclusion.Terms.Part2')}
										</StyledTermsOfServiceSection>
										<ButtonArrange>
											<Form.SubmitButton
												disabled={!!disableSubmit}
												disabledText={disableSubmit || ''}
												onClick={handleSubmitClick(screenConclusion.data.value.requirePayment)}
											>
												{getSubmitButtonLabel(getRequirePayment)}
											</Form.SubmitButton>
										</ButtonArrange>
									</Flex>
								</Flex>
							</Form.Root>
						</Flex>
					</>
				) : null}
			</Flex>
			{payment ? (
				<PaymentSheet
					billId={payment.paymentBillId}
					paymentAmount={payment.paymentAmount}
					onSuccess={handlePaymentSuccess}
					onAbort={handlePaymentAbort}
				/>
			) : null}
			{screenConclusion.showUnavailableTimeSlotDialog ? <TimeSlotNotAvailableDialog /> : null}
		</>
	);
}

function TimeSlotNotAvailableDialog() {
	const viewBooking = useBooking();
	const analyticCtx = useAnalyticContext();

	useEffect(
		function whenMount() {
			analyticCtx.trackEvent(ViewBookingAnalyticActions.OpenedTimeSlotNotAvailableDialog);
		},
		[analyticCtx],
	);

	function handleAbort() {
		analyticCtx.trackEvent(ViewBookingAnalyticActions.ClosedTimeSlotNotAvailableDialog);
		viewBooking.goToScreen(BookingScreen.Location);
	}

	return (
		<DialogAlert
			isOpen
			header={withTranslation('View.Booking.BookingUnavailableDialog.Header')}
			description={withTranslation('View.Booking.BookingUnavailableDialog.Description')}
			abortText={withTranslation('View.Booking.BookingUnavailableDialog.Button')}
			onAbort={handleAbort}
		/>
	);
}

interface Props {
	billId: number;
	paymentAmount: string;
	onSuccess?: () => void;
	onAbort?: () => void;
}

function PaymentSheet({ paymentAmount, billId, onSuccess, onAbort }: Props): JSX.Element {
	return (
		<Sheet onClose={onAbort} defaultOpen variant='full-restricted'>
			<Payment billId={billId} paymentAmount={paymentAmount} onSuccess={onSuccess} onAbort={onAbort} asDialogContent />
		</Sheet>
	);
}

interface TimeSlotInfoProps {
	title?: string;
	timeSlot: {
		price?: string;
		asAgentFor?: string;
		reasonForVisit?: string;
		duration: number;
		physicalLocation: PhysicalLocationApiModel | null;
		resource: ResourceModel | null;
		start: string;
	};
}

export function TimeSlotInfo({ title, timeSlot }: TimeSlotInfoProps) {
	const dateService = useResolve(DateService);
	const startDate = dateService.newDate(timeSlot.start);
	const endDate = startDate.add(timeSlot.duration, 'minutes');

	return (
		<Box
			background={primitiveColorTokens.neutral[100]}
			border={`1px solid ${tokens.color.border.muted}`}
			borderRadius={tokens.borderRadius.small}
			padding={tokens.spacing.x2}
			gap={tokens.spacing.x2}
		>
			{title ? <Text variant='labelLargeSemiBold'>{title}</Text> : null}
			<DataList.Root>
				{timeSlot.price ? (
					<DataList.Item label={withTranslation('Common.Label.Cost')}>
						<BadgeText variant='info'>{timeSlot.price}</BadgeText>
					</DataList.Item>
				) : null}
				{timeSlot.asAgentFor ? (
					<DataList.Item label={withTranslation('Common.Label.AgentFor')}>{timeSlot.asAgentFor}</DataList.Item>
				) : null}
				{timeSlot.reasonForVisit ? (
					<DataList.Item label={withTranslation('Common.Label.Rfv')}>{timeSlot.reasonForVisit}</DataList.Item>
				) : null}
				{timeSlot.price || timeSlot.asAgentFor || timeSlot.reasonForVisit ? <Divider offset={tokens.spacing.x1} /> : null}

				<DataList.Item label={withTranslation('View.Booking.TimeSlotInfo.Label.Date')} icon='calendar'>
					{capitalizeFirstLetter(startDate.format('dddd D MMMM YYYY'))}
				</DataList.Item>
				<DataList.Item label={withTranslation('View.Booking.TimeSlotInfo.Label.Time')} icon='clock'>
					{startDate.format('HH:mm')} - {endDate.format('HH:mm')} ({timeSlot.duration}{' '}
					{withTranslation('Common.Word.Minutes').toLocaleLowerCase()})
				</DataList.Item>
				{timeSlot.physicalLocation ? (
					<DataList.Item label={withTranslation('View.Booking.TimeSlotInfo.Label.Location')} icon='pinMark'>
						<div>
							{timeSlot.physicalLocation.Name}
							<br />
							{timeSlot.physicalLocation.Address}
							<br />
							{timeSlot.physicalLocation.PostalCode}
							<br />
							{timeSlot.physicalLocation.City}
						</div>
					</DataList.Item>
				) : (
					<DataList.Item label={withTranslation('View.Booking.TimeSlotInfo.Label.Type')} icon='video'>
						{withTranslation('View.Booking.TimeSlotInfo.Label.Type.Digital')}
					</DataList.Item>
				)}
				{timeSlot.resource ? (
					<>
						<Divider offset={tokens.spacing.x1} />

						<AvatarInfo
							name={timeSlot.resource.Name ?? undefined}
							title={timeSlot.resource.Title ?? undefined}
							avatarSrc={timeSlot.resource.ProfileImageUrl ?? undefined}
						/>
					</>
				) : null}
			</DataList.Root>
		</Box>
	);
}

interface ParticipantViewModel {
	identityNumber: string;
	identityCode: string;
	name: string;
	isMe: boolean;
	isSubject: boolean;
	selected: boolean;
}

function BookingParticipants() {
	const bookingBasket = useBookingBasket();
	const basket = bookingBasket.getBasket();

	const { data: inviteableData } = useQuery(clientIdentityInviteablePersonsOptions());
	const { data: identityData } = useQuery(clientIdentityGetOptions());

	const [selectedIdentityNumbers, setSelectedIdentityNumbers] = useState<string[]>(() =>
		(basket.additionalParticipants ?? []).map((participant) => participant.IdentityNumber),
	);
	const [isParticipantsSheetOpen, setIsParticipantsSheetOpen] = useState(false);

	// Wait for queries to resolve before rendering
	if (!inviteableData || !identityData) {
		return null;
	}

	const inviteablePersons = inviteableData.Data ?? [];
	const identity = identityData.Data;
	const myIdentityNumber = identity?.NationalIdentityNumber;
	const selectedNumbers = new Set(selectedIdentityNumbers);

	const represented =
		identity?.RepresentingId != null ? identity.CanRepresent?.find((p) => p.Id === identity.RepresentingId) : undefined;
	const subjectIdentityNumber = represented?.IdentityNumber;

	const persons: ParticipantViewModel[] = [];

	for (const p of inviteablePersons) {
		const isMe = p.IdentityNumber === myIdentityNumber;
		const isSubject = subjectIdentityNumber != null && p.IdentityNumber === subjectIdentityNumber;
		persons.push({
			identityNumber: p.IdentityNumber,
			identityCode: p.IdentityCode,
			name: p.Name,
			isMe,
			isSubject,
			selected: isMe || isSubject || selectedNumbers.has(p.IdentityNumber),
		});
	}

	// When the actor logs in as themselves (e.g. a child logging in directly), the inviteable list
	// returns their legal guardians but not the actor – inject a synthetic "me" row so the user
	// always shows up as a conversation participant.
	if (identity?.RepresentingId == null && myIdentityNumber != null && !persons.some((p) => p.isMe) && persons.length > 0) {
		const meName = [identity?.FirstName, identity?.LastName].filter(Boolean).join(' ');
		persons.unshift({
			identityNumber: myIdentityNumber,
			identityCode: identity?.Nationality ?? '',
			name: meName,
			isMe: true,
			isSubject: false,
			selected: true,
		});
	}

	// The represented subject always has access, so show it (read-only, selected) if it is not
	// already part of the inviteable persons list.
	if (represented && !persons.some((p) => p.identityNumber === represented.IdentityNumber)) {
		const name = [represented.GivenName, represented.Surname].filter(Boolean).join(' ') || represented.CommonName;
		persons.push({
			identityNumber: represented.IdentityNumber,
			identityCode: represented.IdentityCode,
			name,
			isMe: false,
			isSubject: true,
			selected: true,
		});
	}

	const mePersons = persons.filter((p) => p.isMe);
	const subjectPersons = persons.filter((p) => p.isSubject && !p.isMe);
	const otherPersons = persons.filter((p) => !p.isMe && !p.isSubject);

	// Only show if there are participants besides "me"
	if (subjectPersons.length === 0 && otherPersons.length === 0) {
		return null;
	}

	return (
		<>
			<Divider />
			<Flex gap={tokens.spacing.x1}>
				<Flex direction='row' justify='space-between' align='center'>
					<Text variant='labelSemiBold' color={tokens.color.content.default}>
						{withTranslation('Common.Label.Participants')}
					</Text>
					<TextLink
						as='button'
						type='button'
						size='small'
						onClick={() => setIsParticipantsSheetOpen(true)}
						style={{ color: tokens.color.content.interactive }}
					>
						{withTranslation('View.CreateNewMessage.ScreenConclusion.ChangeParticipants')}
					</TextLink>
				</Flex>
				<Flex gap={tokens.spacing.x0_5}>
					{mePersons.map((person) => (
						<Flex key={person.identityNumber} direction='row' align='center' gap={tokens.spacing.x1}>
							<Icon type='checkCircleFilled' size='small' color={tokens.color.success.bgActive} />
							<Text variant='labelSemiBold' color={tokens.color.content.default}>
								{withTranslation('View.Booking.ScreenAccess.Option.Me', { name: person.name })}
							</Text>
						</Flex>
					))}
					{subjectPersons.map((person) => (
						<Flex key={person.identityNumber} direction='row' align='center' gap={tokens.spacing.x1}>
							<Icon type='checkCircleFilled' size='small' color={tokens.color.success.bgActive} />
							<Text variant='labelSemiBold' color={tokens.color.content.default}>
								{person.name}
							</Text>
						</Flex>
					))}
					{otherPersons.map((person) => (
						<Flex key={person.identityNumber} direction='row' align='center' gap={tokens.spacing.x1}>
							<Icon
								type={person.selected ? 'checkCircleFilled' : 'prohibited'}
								size='small'
								color={person.selected ? tokens.color.success.bgActive : tokens.color.content.default}
							/>
							<Text variant={person.selected ? 'labelSemiBold' : 'labelRegular'} color={tokens.color.content.default}>
								{person.name}
							</Text>
						</Flex>
					))}
				</Flex>
			</Flex>
			{isParticipantsSheetOpen ? (
				<ChangeParticipantsSheet
					persons={persons}
					onClose={() => setIsParticipantsSheetOpen(false)}
					onSaved={setSelectedIdentityNumbers}
				/>
			) : null}
		</>
	);
}

const StyledChangeParticipantsInner = styled.div`
	width: 100%;
	max-width: 600px;
	margin: 0 auto;
	padding: 0 ${tokens.spacing.x2};
	box-sizing: border-box;
`;

interface ChangeParticipantsSheetProps {
	persons: ParticipantViewModel[];
	onClose: () => void;
	onSaved: (selectedIdentityNumbers: string[]) => void;
}

function ChangeParticipantsSheet({ persons, onClose, onSaved }: ChangeParticipantsSheetProps) {
	const bookingBasket = useBookingBasket();

	const mePersons = persons.filter((p) => p.isMe);
	const subjectPersons = persons.filter((p) => p.isSubject && !p.isMe);
	const otherPersons = persons.filter((p) => !p.isMe && !p.isSubject);

	const defaultAccess = [
		...new Set([
			...mePersons.map((p) => p.identityNumber),
			...subjectPersons.map((p) => p.identityNumber),
			...otherPersons.filter((p) => p.selected).map((p) => p.identityNumber),
		]),
	];

	function handleSubmit(formData: CellulaFormSubmitData) {
		const selected = new Set<string>(formData.access);
		const additionalParticipants = otherPersons
			.filter((person) => selected.has(person.identityNumber))
			.map((person) => ({
				IdentityCode: person.identityCode,
				IdentityNumber: person.identityNumber,
			}));

		bookingBasket.setBasket({ additionalParticipants });
		onSaved(additionalParticipants.map((participant) => participant.IdentityNumber));
		onClose();
	}

	return (
		<Sheet defaultOpen onClose={onClose} variant='half' label={withTranslation('View.Booking.ScreenAccess.Title')}>
			<StyledChangeParticipantsInner>
				<Flex gap={tokens.spacing.x2}>
					<Title title={withTranslation('View.Booking.ScreenAccess.Title')} titleSize='small' />
					<Form.Root options={{ defaultValues: { access: defaultAccess }, mode: 'onBlur' }} onSubmit={handleSubmit}>
						<Flex grow={1} gap={tokens.spacing.x2}>
							<InputCheckboxGroup
								label={withTranslation('View.Booking.ScreenAccess.Input.Label.access')}
								variant='outlined'
								required
								name='access'
							>
								{mePersons.map((person) => (
									<InputCheckbox
										key={person.identityNumber}
										label={withTranslation('View.Booking.ScreenAccess.Option.Me', { name: person.name })}
										value={person.identityNumber}
										readOnly
										checked
									/>
								))}
								{subjectPersons.map((person) => (
									<InputCheckbox key={person.identityNumber} label={person.name} value={person.identityNumber} readOnly checked />
								))}
								{otherPersons.map((person) => (
									<InputCheckbox key={person.identityNumber} label={person.name} value={person.identityNumber} />
								))}
							</InputCheckboxGroup>
						</Flex>
						<ButtonArrange>
							<Form.SubmitButton>{withTranslation('PW.Common.Next')}</Form.SubmitButton>
						</ButtonArrange>
					</Form.Root>
				</Flex>
			</StyledChangeParticipantsInner>
		</Sheet>
	);
}
